import React, { createContext, useContext } from 'react';
import { DropItemPosition } from '@/utils/drag-and-drop';
import ComparisonItem from '@/types/comparison-item';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import Locale from '@/types/locale';
import * as ProposalNS from '../../Proposal';
import ProposalPaperFormat from '@/types/proposal-paper-format';
import ProposalOrientation from '@/types/proposal-orientation';

export interface ProposalActions {
  // Toggle functions
  onToggleHiddenLayout: (hiddenLayout: string, selected: boolean) => void;
  onToggleSection: (section: string, selected: boolean) => void;
  onToggleDivision: (division: string, selected: boolean) => void;
  onToggleFundProfiles: (value: boolean) => void;
  onToggleGroupProfiles: (value: boolean) => void;
  onTogglePortfolioProfiles: (value: boolean) => void;
  onToggleShortHistory: (value: boolean) => void;
  onToggleGifBaseScenario: (value: boolean) => void;

  // Item selection functions
  onItemsSelection: (items: ComparisonItem[]) => void;
  onItemPurposeFlagUpdate: (itemId: string, flags: string[]) => void;
  onGifItemsSelection: (items: BMOGIFItem[]) => void;

  // Style and template functions
  onHeaderPatternChange: (pattern: string) => void;
  onHeaderColorChange: (color: string) => void;
  onCoBrandingColorChange: (selectedColor: string, key: string) => void;
  onOrientationChange: (orientation: ProposalOrientation) => void;
  onPaperFormatChange: (format: ProposalPaperFormat) => void;
  onLocaleChange: (locale: Locale) => void;
  onProposalStyleOptionsChange: (styleOptions: ProposalNS.ProposalStyleOptionsV2) => void;

  // Other functions
  onChangeTemplate: () => void;
  onEditBrandingInfo: () => void;
  onChangeItemOrder: (itemId: string, referenceId: string, position?: DropItemPosition) => void;
  onChangeContentOrder: (itemId: string, referenceId: string, position?: DropItemPosition) => void;
  downloadPdf: () => void;
  onJumpToPage: (section: string, scrollOptions?: ScrollIntoViewOptions) => void;
}

const ProposalActionsContext = createContext<ProposalActions | null>(null);

export const ProposalActionsProvider: React.FC<{
  children: React.ReactNode;
  actions: ProposalActions;
}> = ({ children, actions }) => <ProposalActionsContext.Provider value={actions}>{children}</ProposalActionsContext.Provider>;

export const useProposalActions = (): ProposalActions => {
  const context = useContext(ProposalActionsContext);
  if (!context) {
    throw new Error('useProposalActions must be used within a ProposalActionsProvider');
  }
  return context;
};
